import { useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarClock, ChevronLeft, ChevronRight, Download, Unlink, Wallet } from 'lucide-react';
import { getChannexUsage } from '@/lib/api/admin';
import type { ChannexRates, ChannexUsageHotel, ChannexUsageOrganization, ChannexUsageReport } from '@/lib/api/types';
import { downloadCsv } from '@/lib/csv';
import { formatCompact, formatMoney, formatNumber, formatPeriod, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { daysUntil, periodOf, shiftPeriod } from '@/lib/period';
import { Badge } from '@/components/ui/badge';
import { Button, IconButton } from '@/components/ui/button';
import { Card, CardHeader, Stat } from '@/components/ui/card';
import { EmptyState, Notice, QueryError, Skeleton } from '@/components/ui/feedback';
import { PageHeader } from '@/components/ui/page';
import { FilterSelect } from '@/components/ui/input';
import { TableToolbar } from '@/components/ui/toolbar';
import { DivergingBars, HBars } from '@/components/charts/lazy';
import { pricingLine } from './pricing';

const usd = (v: number) => formatMoney(v, 'USD');
const uzs = (v: number) => formatMoney(v, 'UZS');
const uzsShort = (v: number) => `${v < 0 ? '−' : ''}${formatCompact(Math.abs(v))}`;

/**
 * What the channel manager costs us this month, against what it earns.
 *
 * The number this screen exists for is the pairing, not the total: plan
 * revenue is flat per organization while Channex bills per property (an
 * apartment property per unit), so a
 * client with three hotels on one plan is where cost quietly overtakes
 * revenue. The margin chart puts every organization on one axis around zero.
 *
 * Three things it refuses to round off:
 *  - an unreconciled month reads "not measured", never a confident zero;
 *  - while any property's channel count is unreported the total is a FLOOR,
 *    and the screen says so instead of presenting it as the bill;
 *  - margin appears only when the backend has a USD rate. Revenue is in UZS
 *    and the cost in USD; without a rate both sides are shown raw.
 */
export function ChannexPage() {
  const thisMonth = periodOf(new Date());
  const [period, setPeriod] = useUrlParam('period', thisMonth);
  const previous = shiftPeriod(period, -1);
  const report = useQuery({ queryKey: ['admin', 'channex-usage', period], queryFn: () => getChannexUsage(period) });
  const prior = useQuery({ queryKey: ['admin', 'channex-usage', previous], queryFn: () => getChannexUsage(previous) });
  const data = report.data;
  const [q, setQ] = useUrlParam('q');
  const [only, setOnly] = useUrlParam('only', 'all');
  const [order, setOrder] = useUrlParam('order', 'cost');
  const query = useDebounced(q.trim().toLowerCase(), 150);

  const organizations = useMemo(() => {
    let rows = data?.organizations ?? [];
    if (query) rows = rows.filter((o) => `${o.name} ${o.plan ?? ''} ${o.hotels.map((h) => h.title ?? h.propertyId).join(' ')}`.toLowerCase().includes(query));
    if (only === 'billable') rows = rows.filter((o) => o.billable > 0);
    if (only === 'negative') rows = rows.filter((o) => o.marginUzs !== null && o.marginUzs < 0);
    if (only === 'warnings') rows = rows.filter((o) => o.hotels.some((h) => h.channelCount === null || daysUntil(h.removalDate) !== null));
    const by: Record<string, (a: ChannexUsageOrganization, b: ChannexUsageOrganization) => number> = {
      cost: (a, b) => b.costUsd - a.costUsd,
      margin: (a, b) => (a.marginUzs ?? Infinity) - (b.marginUzs ?? Infinity),
      name: (a, b) => a.name.localeCompare(b.name, 'ru'),
    };
    return [...rows].sort(by[order] ?? by.cost);
  }, [data, query, only, order]);

  const delta = (now: number, before: number | undefined, lowerIsGood: boolean) => {
    if (before === undefined || !prior.data || prior.data.totals.properties === 0) return undefined;
    const diff = now - before;
    if (diff === 0) return { text: 'без изменений', good: null };
    const text = `${diff > 0 ? '+' : '−'}${formatNumber(Math.abs(diff), 2)} к ${formatPeriod(previous).split(' ')[0].toLowerCase()}`;
    return { text, good: lowerIsGood ? diff < 0 : diff > 0 };
  };

  const expiring = useMemo(() => data
    ? [...data.organizations.flatMap((o) => o.hotels), ...data.orphans].filter((h) => { const d = daysUntil(h.removalDate); return d !== null && d <= 45; })
    : [], [data]);

  const costBars = useMemo(() => data ? [
    ...data.organizations.filter((o) => o.costUsd > 0).map((o) => ({ name: o.name, value: o.costUsd, detail: plural(o.billable, ['платный отель', 'платных отеля', 'платных отелей']) })),
    ...(data.orphans.length ? [{ name: 'Без владельца', value: data.orphans.reduce((s, h) => s + h.costUsd, 0), detail: plural(data.orphans.length, ['объект', 'объекта', 'объектов']) }] : []),
  ] : [], [data]);

  const marginBars = useMemo(() => data ? data.organizations.filter((o) => o.marginUzs !== null).map((o) => ({ name: o.name, value: o.marginUzs as number, detail: `платит ${o.revenue ? formatMoney(o.revenue.amount, o.revenue.currency) : '—'}, стоит ${usd(o.costUsd)}` })) : [], [data]);

  const exportCsv = () => data && downloadCsv(`channex-${period}.csv`,
    ['Организация', 'organizationId', 'Тариф', 'Платит', 'Валюта', 'Платных отелей', 'Стоит, USD', 'Маржа, UZS', 'Объект', 'propertyId', 'hotelId', 'Тип', 'Юнитов', 'Каналов', 'Пик', 'Платный', 'Стоимость, USD', 'Удаление'],
    [
      ...data.organizations.flatMap((o) => o.hotels.map((h) => [o.name, o.organizationId, o.plan, o.revenue?.amount, o.revenue?.currency, o.billable, o.costUsd, o.marginUzs, h.title, h.propertyId, h.hotelId, h.propertyType, h.unitCount, h.channelCount, h.peakChannels, h.billable ? 'да' : 'нет', h.costUsd, h.removalDate])),
      ...data.orphans.map((h) => ['— без владельца —', '', '', '', '', '', '', '', h.title, h.propertyId, h.hotelId, h.propertyType, h.unitCount, h.channelCount, h.peakChannels, h.billable ? 'да' : 'нет', h.costUsd, h.removalDate]),
    ]);

  return (
    <>
      <PageHeader
        title="Channex: расходы"
        subtitle="Что channel manager стоит нам за месяц — против того, что каждая организация платит нам."
        action={(
          <>
            <Button variant="outline" size="sm" icon={<Download size={14} />} onClick={exportCsv} disabled={!data || data.totals.properties === 0}>CSV</Button>
            {/* Forward is capped at the current month: a future period has nothing to reconcile. */}
            <div className="flex items-center gap-1 rounded-lg border border-line bg-surface p-1">
              <IconButton size="sm" title="Предыдущий месяц" onClick={() => setPeriod(shiftPeriod(period, -1))}><ChevronLeft size={15} /></IconButton>
              <span className="min-w-[140px] text-center text-sm font-semibold text-ink">{formatPeriod(period)}</span>
              <IconButton size="sm" title="Следующий месяц" disabled={period >= thisMonth} onClick={() => setPeriod(shiftPeriod(period, 1))}><ChevronRight size={15} /></IconButton>
            </div>
          </>
        )}
      />

      {report.isPending && <div className="flex gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[132px] flex-1" />)}</div>}
      {report.isError && <QueryError error={report.error} onRetry={() => report.refetch()} />}

      {data && data.totals.properties === 0 && (
        <EmptyState icon={<Wallet size={28} />} title="За этот месяц сверка не проходила" description="Месяц наполняет ночная сверка с Channex. Пусто — значит не измеряли, а не «расходов не было»."
          action={period < thisMonth ? <Button size="sm" variant="ghost" onClick={() => setPeriod(thisMonth)}>К текущему месяцу</Button> : undefined} />
      )}

      {data && data.totals.properties > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Stat label="Расход за месяц" value={usd(data.totals.totalCostUsd)} hint={`${usd(data.rates.platformFeeUsd)} платформа + ${usd(data.totals.propertyCostUsd)} за ${plural(data.totals.billable, ['объект', 'объекта', 'объектов'])}`} icon={<Wallet size={18} />} delta={delta(data.totals.totalCostUsd, prior.data?.totals.totalCostUsd, true)} />
            <Stat label="Платных объектов" value={formatNumber(data.totals.billable)} hint={`из ${formatNumber(data.totals.properties)} на аккаунте`} icon={<Building2 size={18} />} tone="ok" delta={delta(data.totals.billable, prior.data?.totals.billable, false)} />
            <Stat label="Сироты" value={formatNumber(data.totals.orphans)} hint="объекты без наших подключений" icon={<Unlink size={18} />} tone={data.totals.orphans > 0 ? 'danger' : 'neutral'} delta={delta(data.totals.orphans, prior.data?.totals.orphans, true)} />
            <TotalMargin data={data} />
          </div>

          {data.totals.unknown > 0 && (
            <Notice tone="warn" title={`${plural(data.totals.unknown, ['объект', 'объекта', 'объектов'])} без данных о каналах`}>Channex не сообщил число каналов, поэтому итог — нижняя граница, а не счёт.</Notice>
          )}
          {data.totals.unknownUnits > 0 && (
            <Notice tone="warn" title={`Апартаменты без числа юнитов: ${plural(data.totals.unknownUnits, ['объект', 'объекта', 'объектов'])}`}>Channex берёт за апартаменты {usd(data.rates.unitFeeUsd)} за юнит, но юниты ещё не сверены — пока взято {usd(data.rates.propertyFeeUsd)} за объект. Ночная сверка их досчитает.</Notice>
          )}
          {expiring.length > 0 && (
            <Notice tone="danger" icon={<CalendarClock size={16} />} title={`${plural(expiring.length, ['объект', 'объекта', 'объектов'])} скоро удалят`}>
              <p>Channex удаляет объект без единого канала через 90 дней — вместе с типами номеров и тарифами.</p>
              <p className="mt-1">{expiring.map((h) => h.title ?? h.propertyId).join(', ')}</p>
            </Notice>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="Расход по организациям" subtitle="Сколько стоит нам каждый клиент в этом месяце, USD." />
              <HBars data={costBars} format={usd} />
            </Card>
            <Card>
              <CardHeader title="Маржа по организациям" subtitle={data.rates.usdToUzs !== null ? `Платит нам минус стоит нам, UZS по курсу ${formatNumber(data.rates.usdToUzs)}.` : 'Курс USD не задан — маржа не считается.'} />
              {data.rates.usdToUzs !== null ? <DivergingBars data={marginBars} format={uzs} label={uzsShort} /> : <p className="py-6 text-center text-xs text-faint">Задайте USD_TO_UZS_RATE в .env бэкенда.</p>}
            </Card>
          </div>

          <section>
            <h2 className="mb-3 text-sm font-bold text-ink">По организациям</h2>
            <TableToolbar search={{ value: q, onChange: setQ, placeholder: 'Организация, тариф или объект' }} shown={organizations.length} total={data.organizations.length} onReset={q || only !== 'all' ? () => { setQ(''); setOnly('all'); } : null}>
              <FilterSelect ariaLabel="Показать" value={only} onChange={setOnly} options={[{ value: 'all', label: 'Все организации' }, { value: 'billable', label: 'С платными объектами' }, { value: 'negative', label: 'С отрицательной маржой' }, { value: 'warnings', label: 'С предупреждениями' }]} />
              <FilterSelect ariaLabel="Порядок" value={order} onChange={setOrder} options={[{ value: 'cost', label: 'По расходу' }, { value: 'margin', label: 'По марже' }, { value: 'name', label: 'По названию' }]} />
            </TableToolbar>
            {organizations.length === 0 ? <EmptyState icon={<Building2 size={28} />} title="Ничего не подошло под фильтры" /> : (
              <div className="space-y-4">{organizations.map((o) => <OrganizationCard key={o.organizationId} organization={o} rates={data.rates} />)}</div>
            )}
          </section>

          {data.orphans.length > 0 && (
            <Card>
              <div className="flex items-center gap-2"><Unlink size={16} className="text-danger" /><h2 className="text-base font-bold text-danger">Объекты без владельца</h2></div>
              <p className="mt-1 text-xs text-muted">Живы и платны в Channex, но ни одно наше подключение на них не указывает.</p>
              <ul className="mt-2 divide-y divide-line">{data.orphans.map((h) => <HotelRow key={h.propertyId} hotel={h} rates={data.rates} />)}</ul>
            </Card>
          )}

          {data.rates.usdToUzs === null && <p className="px-1 text-[11px] text-faint">Курс USD не задан (USD_TO_UZS_RATE) — маржа не считается, стороны показаны раздельно.</p>}
        </div>
      )}
    </>
  );
}

function TotalMargin({ data }: { data: ChannexUsageReport }) {
  if (data.rates.usdToUzs === null) return null;
  const margin = data.organizations.reduce((s, o) => s + (o.marginUzs ?? 0), 0);
  const revenue = data.organizations.reduce((s, o) => s + (o.revenue?.amount ?? 0), 0);
  return <Stat label="Маржа за месяц" value={uzs(margin)} hint={`выручка ${uzs(revenue)} − расход ${usd(data.totals.totalCostUsd)}`} icon={<Wallet size={18} />} tone={margin >= 0 ? 'ok' : 'danger'} />;
}

function OrganizationCard({ organization, rates }: { organization: ChannexUsageOrganization; rates: ChannexRates }) {
  const { revenue, marginUzs } = organization;
  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Link to={`/organizations/${organization.organizationId}`} className="text-base font-bold text-ink hover:text-accent-ink hover:underline">{organization.name}</Link>
          <p className="text-xs text-muted">{plural(organization.billable, ['платный отель', 'платных отеля', 'платных отелей'])}</p>
        </div>
        {organization.plan && <Badge mono>{organization.plan}</Badge>}
      </div>
      <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
        <div><dt className="text-[11px] text-faint">Платит нам</dt><dd className="text-sm font-semibold text-ink tabular-nums">{revenue ? formatMoney(revenue.amount, revenue.currency) : 'нет подписки'}</dd></div>
        <div><dt className="text-[11px] text-faint">Стоит нам</dt><dd className="text-sm font-semibold text-ink tabular-nums">{usd(organization.costUsd)}</dd></div>
        {marginUzs !== null && <div><dt className="text-[11px] text-faint">Маржа</dt><dd className={`text-sm font-bold tabular-nums ${marginUzs >= 0 ? 'text-ok' : 'text-danger'}`}>{uzs(marginUzs)}</dd></div>}
      </dl>
      <ul className="mt-2 divide-y divide-line">{organization.hotels.map((h) => <HotelRow key={h.propertyId} hotel={h} rates={rates} />)}</ul>
    </Card>
  );
}

function HotelRow({ hotel, rates }: { hotel: ChannexUsageHotel; rates: ChannexRates }) {
  const removal = daysUntil(hotel.removalDate);
  const name = hotel.title ?? hotel.propertyId;
  return (
    <li className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        {hotel.hotelId ? <Link to={`/hotels/${hotel.hotelId}`} className="truncate text-sm text-ink hover:text-accent-ink hover:underline">{name}</Link> : <p className="truncate text-sm text-ink">{name}</p>}
        <p className="text-[11px] text-faint">
          {hotel.channelCount === null ? 'число каналов неизвестно' : plural(hotel.channelCount, ['канал', 'канала', 'каналов'])}
          {hotel.peakChannels > (hotel.channelCount ?? 0) && ` · пик ${formatNumber(hotel.peakChannels)}`}
          {` · ${pricingLine(hotel, rates)}`}
        </p>
      </div>
      {removal !== null && (
        <Badge tone={removal <= 30 ? 'danger' : 'warn'}>{removal < 0 ? `удалён ${formatNumber(-removal)} дн. назад` : removal === 0 ? 'удаляется сегодня' : `снос через ${formatNumber(removal)} дн.`}</Badge>
      )}
      <Badge tone={hotel.billable ? 'accent' : 'neutral'}>{hotel.billable ? usd(hotel.costUsd) : 'бесплатно'}</Badge>
    </li>
  );
}
